import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import { FaHeart, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "../css/Accommodations.css";
import { formatRupiah } from "../utils/formatRupiah";
import { useTranslation } from "react-i18next";

const RoomLogged = () => {
  const { t, i18n } = useTranslation();
  const [favorites, setFavorites] = useState({});
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchRoomTypes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/room-types`,
          {
            headers: {
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (response.data && Array.isArray(response.data.data)) {
          const normalized = response.data.data.map((rt) => ({
            id: rt.room_type_id ?? rt.id,
            room_type_code: rt.room_type_code ?? null,
            name:
              rt.room_type_name ??
              rt.room_type_name ??
              rt.name ??
              `Room ${rt.room_type_id}`,
            price: rt.price ?? rt.price_weekday ?? 0,
            currency: rt.currency ?? "IDR",
            available_rooms: rt.available_rooms ?? 0,
            short_description: rt.short_description ?? rt.description ?? "",
            thumbnail_url: rt.thumbnail_url ?? null,
            photos: Array.isArray(rt.room_photos)
              ? rt.room_photos.map((p) => ({
                  id: p.id,
                  url: p.photo_url,
                  photo_type: p.photo_type,
                }))
              : [],
            __raw: rt,
          }));

          setRoomTypes(normalized);

          const storedFavorites =
            JSON.parse(localStorage.getItem("favorites")) || {};
          setFavorites(storedFavorites);
        } else {
          setRoomTypes([]);
          setError(t("errorFetchingData"));
        }
      } catch (err) {
        setError(t("errorFetchingData"));
      } finally {
        setLoading(false);
      }
    };

    fetchRoomTypes();
  }, [i18n.language, t]);

  const toggleFavorite = async (roomTypeId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    const isFavorited = favorites[roomTypeId];

    try {
      if (isFavorited) {
        await axios.delete(
          `${process.env.REACT_APP_APP_URL}/favorites/${roomTypeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            },
          }
        );

        Swal.fire({
          title: "Success",
          text: "Room removed from favorites!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_APP_URL}/favorites`,
          { room_type_id: roomTypeId },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            },
          }
        );

        Swal.fire({
          title: "Success",
          text: "Room added to favorites!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      setFavorites((prevFavorites) => ({
        ...prevFavorites,
        [roomTypeId]: !isFavorited,
      }));
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Failed to update favorites!",
        icon: "error",
      });
    }
  };

  return (
    <section className="container mt-5">
      <h2 className="fw-bold">{t("ourAccommodation")}</h2>
      <p>{t("descriptionAccommodation")}</p>

      {loading && <LoadingSkeleton />}
      {error && <ErrorMessage message={error} />}

      {!loading && !error && (
        <div className="row">
          {roomTypes.length > 0 ? (
            roomTypes.map((roomType) => {
              const imageUrl = roomType.thumbnail_url
                ? roomType.thumbnail_url
                : roomType.photos && roomType.photos.length > 0
                ? roomType.photos[0].url
                : "/assets/placeholder.jpg";

              const name = roomType.name;
              const price = roomType.price || roomType.price_weekday || 0;
              const rating = roomType.rating?.average || 0;

              return (
                <div className="col-md-3 col-sm-6 mb-4" key={roomType.id}>
                  <Card className="shadow-lg rounded-4 overflow-hidden position-relative card-hover">
                    <div className="position-relative">
                      <Card.Img
                        variant="top"
                        src={imageUrl}
                        className="card-img"
                        alt={name}
                      />
                      <Button
                        variant="light"
                        className="position-absolute top-0 end-0 m-2 p-2 rounded-circle shadow-sm heart-btn"
                        onClick={() => toggleFavorite(roomType.id)}
                      >
                        <FaHeart
                          size={20}
                          className={
                            favorites[roomType.id]
                              ? "text-danger"
                              : "text-muted"
                          }
                        />
                      </Button>
                    </div>
                    <Card.Body>
                      <Card.Title className="fw-bold">{name}</Card.Title>
                      <RatingStars rating={rating} />
                      <div className="d-flex">
                        <span className="text-muted fw-bold fs-6">
                          {formatRupiah(price)}
                        </span>
                      </div>
                      <Button
                        variant="success"
                        className="w-100 mt-3"
                        onClick={() => navigate(`/detail-room/${roomType.id}`)}
                      >
                        {t("bookNow")}
                      </Button>
                    </Card.Body>
                  </Card>
                </div>
              );
            })
          ) : (
            <p className="text-center mt-4">{t("noAccommodations")}</p>
          )}
        </div>
      )}
    </section>
  );
};

const LoadingSkeleton = () => (
  <div className="row">
    {[...Array(4)].map((_, idx) => (
      <div className="col-md-3 col-sm-6 mb-4" key={idx}>
        <div className="card shimmer-card shadow-lg rounded-4 overflow-hidden">
          <div className="shimmer-img"></div>
          <div className="p-3">
            <div className="shimmer-title"></div>
            <div className="shimmer-line"></div>
            <div className="shimmer-line"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="container mt-5 text-center text-danger">
    <p>{message}</p>
  </div>
);

const RatingStars = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return (
    <div className="d-flex mb-2">
      {Array.from({ length: fullStars }).map((_, i) => (
        <FaStar key={`full-${i}`} className="text-warning" />
      ))}
      {halfStar && <FaStarHalfAlt key="half" className="text-warning" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <FaStar key={`empty-${i}`} className="text-muted" />
      ))}
    </div>
  );
};

export default RoomLogged;
